import "@zebra-fed/icons/Enterprise/index.css";
import Snackbar from "@material-ui/core/Snackbar";
import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
} from "@material-ui/icons";
import { Tooltip, Checkbox } from "@material-ui/core";
import { useTranslation } from "react-i18next";

// Internal imports
import { AuthContext } from "../../authentication/Auth";
import { SSOSettingsContext } from "../../authentication/SSOSettings";
import { MessageResponseContext } from "../../data/MessageLocalizationProvider";
import firebaseUtil from "../../data/FirebaseUtil";
import {
  fetchSites,
  getTotalCount,
  fetchLastPage,
  getAppSettings,
  getSSOParameterMapping,
} from "./siteQueries";
import { getURLFromGCSBucket } from "../commonUtils/ExportUtils";
import app from "../../authentication/firebaseStart";
import { SiteService } from "../../Services/SiteService";
import {
  ROLES,
  RESOURCES,
  usePermissions,
  withRBAC,
} from "../../authentication/rbac";

// Components
import AddNewSiteModal from "../../components/modals/AddNewSiteModal";
import { EditSiteModal } from "../../components/modals/EditSiteModal";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import CsvCompModal from "../../components/modals/CsvCompModal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import AlertModal from "../../components/modals/AlertModal";
import ActionDropDown from "../../components/ActionDropDown";
import DropdownActions from '../../components/DropdownSimple';
import { ButtonGroup, CommonButton } from "../../components/common/Button";
import BottomNavLinks from "../../components/BottomNavLinks";
import NumberToDate from "../users/components/NumberToDate";
import Toolbar from "../../components/common/Toolbar";
import Table from "../../components/common/Table";
import {
  SitesConfigData,
  SitesConfigDataWithIP,
} from "../../components/SitesCsvConfig";

import {
  ContentWrapper,
  CommonPageHeader,
  CommonPageTitle,
  SnackbarContent,
} from "../../styled-components/shared";

const MainContent = styled.div`
  border-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }

  .MuiTableHead-root {
    display: contents;
  }
`;

const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: "center";
  text-align: ${(props) => (props.left ? "left" : "center")};
`;

const ToolbarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const DEFAULT_ROWS_PER_PAGE = 10;

const imgStyleLoader = {
  width: 18,
  height: 18,
  marginTop: 6,
  marginLeft: -15,
  marginRight: 5,
};

const firestore = app.firestore();

function ManageSitesPage() {
  const [data, setData] = useState([]);
  const [apsData, setApsData] = useState([]);
  const [error, setError] = useState(false);
  const [snackbarVisible, setSnackBarVisible] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [deletedSite, setDeletedSite] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [searchField, setSearchField] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isSSOMappingEnabled, setIsSSOMappingEnabled] = useState(false);

  const [orderBy, setOrderBy] = useState("lastUpdatedAt");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [cursors, setCursors] = useState(null);
  const [searchString, setSearchString] = useState("");
  const [deletedSiteName, setDeletedSiteName] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [exportBtnState, setexportBtnState] = useState("none");
  const [errorMessage, setErrorMessage] = useState("");

  const [selectAll, setSelectAll] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [confirmPopupData, setconfirmPopupData] = useState([]);
  const [enableModDelete, setEnableModDelete] = useState(false);
  const [manageBtnValue, setManageBtnValue] = useState({});

  const context = useContext(AuthContext);
  const { SSOSettings } = useContext(SSOSettingsContext);
  const { t } = useTranslation();

  const addSiteModalRef = useRef();
  const confirmModalRef = useRef();
  const alertModalRef = useRef();
  const editSiteModalRef = useRef();
  const confirmationModalRef = useRef();

  const isSnackbarFailure = useRef();
  const CsvCompModalRef = useRef();
  const snackBarContent = useRef();
  const service = new SiteService();

  const companyID = context.currentUser.claims.companyID;

  const {
    canCreateSite,
    canReadSite,
    canUpdateSite,
    canDeleteSite,
    // canCreateEmailConfiguration,
    // canReadEmailConfiguration,
    // canUpdateEmailConfiguration,
    // canDeleteEmailConfiguration,
  } = usePermissions();

  useEffect(() => {
    if (!canReadSite) {
      return; // TODO: redirect/show unauthorized message
    }
    initManageSites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchText) {
      setOrderBy("lastUpdatedAt");
      setSearchField(null);
    } else {
      setOrderBy(searchField);
    }
  }, [searchText, searchField]);

  useEffect(() => {
    if (selectedSite) {
      addSiteModalRef.current.toggleDialog();
    }
  }, [selectedSite]);

  // Helper Functions
  async function initManageSites() {
    const appSettings = await getAppSettings(context);
    setLoggedInUser(
      appSettings?.SSOActivationStatus === true
        ? context.currentUser.uid
        : context.currentUser.email
    );
    setApsData(appSettings);
    getData(pageSize, orderBy, orderDirection);
  }

  function formatTimeData(sites) {
    return sites.map((s) => {
      return {
        displayScheduledReportTime: get12hrsFormat(s.scheduledReportTime),
        ...s,
      };
    });
  }

  async function getData(
    currentPageSize,
    orderBy = "lastUpdatedAt",
    orderDirection = "desc",
    shouldRefreshCount = false,
    currentSearchText = searchText,
    currentSearchField = searchField
  ) {
    setIsLoading(true);
    try {
      setCurrentPage(0);
      setIsSSOMappingEnabled(await getSSOParameterMapping(context));

      // Get fresh count if needed
      if (shouldRefreshCount || itemCount === 0) {
        const count = await getTotalCount(context, {
          searchText: currentSearchText,
          searchField: currentSearchField,
          orderDirection,
        });
        setItemCount(count);
      }

      const { sites, cursors: newCursors } = await fetchSites({
        context,
        limit: currentPageSize,
        searchField: currentSearchField,
        searchText: currentSearchText,
        orderBy,
        orderDirection,
        totalCount: itemCount,
      });

      setData(formatTimeData(sites));
      setCursors(newCursors);
      setIsLoading(false);
      if (sites.length > 0) {
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setIsLoading(false);
    }
  }

  async function next() {
    if (!cursors?.last) return;

    setIsLoading(true);
    setCurrentPage((prev) => prev + 1);

    try {
      const { sites, cursors: newCursors } = await fetchSites({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAfterDoc: cursors.last,
        totalCount: itemCount,
      });

      if (sites.length > 0) {
        setData(formatTimeData(sites));
        setCursors(newCursors);
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching next page:", error);
      setIsLoading(false);
    }
  }

  async function prev() {
    if (!cursors?.first) return;

    setIsLoading(true);
    setCurrentPage((prev) => prev - 1);

    try {
      const { sites, cursors: newCursors } = await fetchSites({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        endBeforeDoc: cursors.first,
        totalCount: itemCount,
      });

      if (sites.length > 0) {
        setData(formatTimeData(sites));
        setCursors(newCursors);
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching previous page:", error);
      setIsLoading(false);
    }
  }

  async function handleFirstPage() {
    setIsLoading(true);
    setCurrentPage(0);
    try {
      const { sites, cursors: newCursors } = await fetchSites({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount,
      });

      setData(formatTimeData(sites));
      setCursors(newCursors);
      setIsLoading(false);
      if (sites.length > 0) {
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
    } catch (error) {
      console.error("Error fetching first page:", error);
      setIsLoading(false);
    }
  }

  async function handleLastPage() {
    setIsLoading(true);
    try {
      const result = await fetchLastPage({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount,
      });

      if (result.sites.length > 0) {
        setData(formatTimeData(result.sites));
        setCursors(result.cursors);

        const totalPages = Math.ceil(itemCount / pageSize);
        const lastPageNumber = Math.max(0, totalPages - 1);
        setCurrentPage(lastPageNumber);
        setEnableModDelete(true);
      } else {
        setData([]);
        setCursors(null);
        setCurrentPage(0);
        setEnableModDelete(false);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching last page:", error);
      setData([]);
      setCursors(null);
      setCurrentPage(0);
      setIsLoading(false);
    }
  }

  function handleSearch(text, field) {
    setSearchText(text);
    setSearchField(field);
    handleFetchWithSearch(text, field);
  }

  function handleClearSearch() {
    setSearchText("");
    setSearchField(null);
    getData(pageSize, orderBy, orderDirection, true, "", null);
  }

  async function handleFetchWithSearch(text, field) {
    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null);
    const order = field === "__name__" ? "asc" : orderDirection;
    try {
      // Always get fresh count for new searches
      const count = await getTotalCount(context, {
        searchText: text,
        searchField: field,
        orderDirection: order,
      });
      setItemCount(count);

      const { sites, cursors: newCursors } = await fetchSites({
        context,
        limit: pageSize,
        searchText: text,
        searchField: field,
        orderBy: field,
        orderDirection: order,
        totalCount: count,
      });

      setData(formatTimeData(sites));
      setCursors(newCursors);
      setIsLoading(false);
      if (sites.length > 0) {
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
    } catch (error) {
      console.error("Error searching sites:", error);
      setIsLoading(false);
      setItemCount(0);
      setData([]);
    }
  }

  async function handleSort(field, direction) {
    if (searchText && field !== searchField) return;

    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null); // Reset cursors on sort change

    try {
      setOrderBy(field);
      setOrderDirection(direction);

      const {
        sites,
        totalCount,
        cursors: newCursors,
      } = await fetchSites({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: field,
        orderDirection: direction,
      });

      setData(formatTimeData(sites));
      setItemCount(totalCount);
      setCursors(newCursors);
      setIsLoading(false);
      if (sites.length > 0) {
        setEnableModDelete(true);
      } else {
        setEnableModDelete(false);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setIsLoading(false);
    }
  }

  function onModalClose(modalType, status) {
    setError(false);

    if (modalType === "add site") {
      setSnackBarMessage(t("messages.success.success13"));
    } else if (modalType === "edit site") {
      setSnackBarMessage(t("messages.success.success14"));
    } else {
      setSnackBarMessage(t("messages.success.success15"));
    }

    setSnackBarVisible(true);
    getData(pageSize, orderBy, orderDirection, true);
  }

  async function handleDeleteSite(site) {
    setIsLoading(true);
    try {
      await service.deleteSites(companyID, [site]);
      setError(null);
      setDeletedSite(null);
      setSearchText("");
      onModalClose("Delete Site");
      setIsLoading(false);
    } catch (error) {
      console.error("Error deleting Site:", error);
      setError(error);
      setSnackBarVisible(true);
      setSnackBarMessage(t("Error deleting site"));
      setDeletedSite(null);
      setIsLoading(false);
    }
  }

  function handleCreateSite(event) {
    event.preventDefault();
    event.stopPropagation();
    setSelectedSite(null);
    addSiteModalRef.current.toggleDialog();
  }

  function handleSiteEdit(site) {
    setTimeout(() => {
      setSelectedSite(site);
    }, 0);
  }

  const handleMultiEditSite = () => {
    if (selectedData.length > 0) {
      editSiteModalRef.current.toggleDialog();
    } else {
      showSnackBar();
    }
  };

  const handleMultiDeleteSite = () => {
    if (selectedData.length > 0) {
      confirmationModalRef.current.toggleDialog();
    } else {
      showSnackBar();
    }
  };

  function onCloseSiteModal(status, selectedSite) {
    setSelectedSite(null);
    if (!status) {
      return;
    }
    if (selectedSite) {
      onModalClose("edit site", status);
    } else {
      onModalClose("add site", status);
    }
  }

  const handleRowSelection = (rowData) => {
    const updatedSelection = selectedData.includes(rowData)
      ? selectedData.filter((item) => item !== rowData)
      : [...selectedData, rowData];
    setSelectedData(updatedSelection);

    // If any row is deselected, uncheck the "Select All" checkbox
    if (selectAll && selectedData.includes(rowData)) {
      setSelectAll(false);
    }

    // If all rows are selected manually, check the "Select All" checkbox
    if (updatedSelection.length === data.length) {
      setSelectAll(true);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedData([]);
    } else {
      setSelectedData(data);
    }
    setSelectAll(!selectAll);
  };

  const handleActionChange = (action) => {
    if (action.key == "DELETE") {
      //Set the popup confirmation data. Popup will use this data

      let btnData = [
        { scope: "delete", innerTxt: t("cancel"), proceed: false, class: "" },
        {
          scope: "delete",
          innerTxt: t("confirm"),
          proceed: true,
          class: "confirm",
        },
      ];

      //Set the data for showing popup
      setconfirmPopupData({
        title: t("delete"),
        btnData: btnData,
        msg: `${t("deviceDetail.message")}`,
        topMsg: `${selectedData.length} Site${
          selectedData.length == 1 ? "" : "s"
        } ${t("deviceDetail.selected")}`,
        needcheck: selectAll,
        checktext: t("deleteAllRecordsConfirmMsg"),
      });
      handleMultiDeleteSite();
    } else if (action.key == "UPDATE_SITE") {
      //Show EDIT modal
      if (selectedData.length > 1 && apsData.deviceRegistrationType === 2) {
        setErrorMessage(
          "Multiple sites cannot be updated with auto IP assigment enabled."
        );
        alertModalRef.current.open();
      } else {
        handleMultiEditSite();
      }
    }
  };

  async function handleConfirmationPopup(e) {
    //Hide the popup first and then proceed with delete
    confirmationModalRef.current.toggleDialog();

    if (e.btnData.scope == "delete") {
      if (e.btnData.proceed) {
        //Delete action

        setIsLoading(true);

        await deleteSites(e.checkAll);
        setIsLoading(false);

        var msg = e.checkAll
          ? "All Sites deleted"
          : selectedData.length + " Sites deleted";

        setSnackBarMessage(msg);
        setSnackBarVisible(true);
      }
    } else if (e.btnData.scope == "modify") {
      if (e.btnData.proceed) {
        //Update action
        //Set all seected and call the dialog
      }
    }
  }

  async function deleteSites(deleteAll) {
    //Deelete all the access points
    if (deleteAll) {
      // Delete all the items. If a filter isapplied, should delete only the filtered items
      //Use the current APS query which will dleete  the current selected
      //data based on search text & other parm
      await service.deleteAllSites(companyID, getQuery());
      //Refresh data. Before that clear the selection
      clearSelection();
      //getData(pageSize);
      getData(pageSize, orderBy, orderDirection, true);
    } else {
      //Delete only seleted data..
      var itemsToDelete = selectedData;
      await service.deleteSites(companyID, itemsToDelete);
      let siteInSession = sessionStorage.getItem("selectedSite");
      if (siteInSession && siteInSession != "All Devices") {
        let matchedItem = itemsToDelete.filter(
          (item) => item.siteName == siteInSession
        );
        if (matchedItem.length) {
          sessionStorage.setItem("selectedSite", "All Devices");
        }
      }

      //await apsService.de(companyID,  itemsToDelete);
      //Refresh data. Before that clear the selection
      clearSelection();
      // getData(pageSize);
      getData(pageSize, orderBy, orderDirection, true);
    }
    if (searchText != "") {
      setSearchText("");
    }
  }

  async function showSnackBar() {
    setSnackBarMessage(t("error"));
    setSnackBarVisible(true);
  }

  async function exportcsv() {
    if (data && data.length > 0) {
      let data = {
        entityType: "sites",
        searchField: searchField,
        searchText: searchString,
      };
      setexportBtnState("exporting");
      let response = await firebaseUtil.exportConfiguration(data, t);
      if (response?.status) {
        let url = response.msg.filePath;
        let jobId = response.msg.jobId;
        const ref = firestore
          .collection("tenants")
          .doc(companyID)
          .collection("Jobs")
          .doc(jobId);
        ref.onSnapshot((querySnapshot) => {
          let items = querySnapshot.data();
          if (items.status === 2) {
            async function getURL() {
              setexportBtnState("down");
              let result = await getURLFromGCSBucket(url, companyID, "sites");
              setexportBtnState("none");
            }
            getURL();
          }
        });
      } else {
        setexportBtnState("none");
        showSnackBar();
      }
    } else {
      setErrorMessage("Export cannot be performed for empty records");
      alertModalRef.current.open();
    }
  }

  function bulkUpload() {
    setManageBtnValue({ name: t('csvModal.Add'), value: 'ADD', page: 'SITE' })
    CsvCompModalRef.current.toggleDialog();
  }

  function refreshPageClicked() {
    getData(pageSize, orderBy, orderDirection, true);
  }

  const get12hrsFormat = (e) => {
    const getTwoDigit = (e) => {
      return e.toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });
    };

    if (e) {
      let cronArray = e.split(" ");
      let hr = Number(cronArray[1]);
      let min = Number(cronArray[0]);
      const Hr = getTwoDigit(hr == 0 ? 12 : hr > 12 ? hr - 12 : hr);
      const Mi = getTwoDigit(min);
      const AM_PM = hr >= 12 ? "PM" : "AM";
      return `${Hr}:${Mi} ${AM_PM}`;
    }
    return e;
  };

  function getQuery() {
    console.log("came inside get query");
    var searchString = searchText.toUpperCase();
    let query = firestore
      .collection("tenants")
      .doc(companyID)
      .collection("SiteLocations")
      .orderBy("siteName")
      .startAt(searchString)
      .endAt(searchString + "\uf8ff");
    return query;
  }

  function clearSelection() {
    //Cleawr the selected data
    setSelectedData([]);
    data.forEach((row) => (row.checked = false));
    setSelectAll(false);
  }

  function onChangeManageBtn(buttonValue, buttonName) {
    if (buttonValue === 'MODIFY' || buttonValue === 'DELETE') {
      CsvCompModalRef.current.toggleDialog();
    }
    setIsLoading(false);
    setManageBtnValue({ name: buttonName, value: buttonValue, page: 'SITE' })
  }

  // Table Columns Configuration
  const columns = [
    ...(canUpdateSite || canDeleteSite
      ? [
          {
            title: (
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                color="primary"
              />
            ),
            searchable: false,
            sorting: false,
            render: (rowData) => (
              <Checkbox
                checked={selectedData.includes(rowData)}
                onChange={() => handleRowSelection(rowData)}
                color="primary"
              />
            ),
          },
        ]
      : []),
    {
      title: <CustomHeader left>{t("siteNameText")}</CustomHeader>,
      field: "siteName",
      searchable: true,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.siteName}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("town")}</CustomHeader>,
      field: "town",
      searchable: false,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.town}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("city")}</CustomHeader>,
      field: "city",
      searchable: false,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.city}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("country")}</CustomHeader>,
      field: "country",
      searchable: false,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.country}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("contactName")}</CustomHeader>,
      field: "contact_first_name",
      searchable: false,
      sorting: true,
      render: (rowData) => (
        <CuzCell>{`${rowData.contact_first_name || ""} ${rowData.contact_last_name || ""}`}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t("mobileNo")}</CustomHeader>,
      field: "contact_mobile_number",
      searchable: false,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.contact_mobile_number}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("lastUpdated")}</CustomHeader>,
      field: "lastUpdatedAt",
      sorting: true,
      cellStyle: { textAlign: "center" },
      render: (rowData) => (
        <CuzCell>
          <NumberToDate
            style={{ fontSize: "14px" }}
            timestamp={rowData.lastUpdatedAt}
          />
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t("emailReportingTime")}</CustomHeader>,
      field: "scheduledReportTime",
      sorting: true,
      cellStyle: { textAlign: "center" },
      render: (rowData) => (
        <CuzCell>
          <span>{rowData.displayScheduledReportTime}</span>
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t("site.timeZone")}</CustomHeader>,
      field: "timeZone",
      searchable: false,
      sorting: true,
      render: (rowData) => <CuzCell>{rowData.timeZone}</CuzCell>,
    },
    {
      title: <CustomHeader>{t("site.categories")}</CustomHeader>,
      field: "snapshotReportCategories",
      searchable: false,
      sorting: true,
      render: (rowData) => (
        <Tooltip
          title={rowData.snapshotReportCategories}
          arrow
          placement="bottom-start"
        >
          <CuzCell>{rowData.snapshotReportCategories}</CuzCell>
        </Tooltip>
      ),
    },
    {
      title: <CustomHeader>{t("emailSubscription")}</CustomHeader>,
      field: "emailSubscribed",
      sorting: true,
      cellStyle: { textAlign: "center" },
      render: (rowData) => (
        <CuzCell
          style={{ color: rowData.emailSubscribed ? "#00864F" : "#D70015" }}
        >
          {rowData.emailSubscribed ? t("yes") : t("no")}
        </CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t("site.email_list")}</CustomHeader>,
      field: "emailList",
      searchable: false,
      sorting: true,
      render: (rowData) => (
        <Tooltip title={rowData.emailList} arrow placement="bottom-start">
          <CuzCell>{rowData.emailList}</CuzCell>
        </Tooltip>
      ),
    },
    ...(apsData.deviceRegistrationType === 2
      ? [
          {
            title: <CustomHeader>{t("site.startIPAddress")}</CustomHeader>,
            field: "startIPAddress",
            searchable: false,
            sorting: true,
            render: (rowData) => <CuzCell>{rowData.startIPAddress}</CuzCell>,
          },
          {
            title: <CustomHeader>{t("site.endIPAddress")}</CustomHeader>,
            field: "endIPAddress",
            searchable: false,
            sorting: true,
            render: (rowData) => <CuzCell>{rowData.endIPAddress}</CuzCell>,
          },
        ]
      : []),
    {
      title: <CustomHeader>{t("attachCSV")}</CustomHeader>,
      field: "attachCSV",
      sorting: true,
      cellStyle: { textAlign: "center" },
      render: (rowData) => (
        <CuzCell style={{ color: rowData.attachCSV ? "#00864F" : "#D70015" }}>
          {rowData.attachCSV ? t("yes") : t("no")}
        </CuzCell>
      ),
    },
  ];

  return (
    <ContentWrapper>
      <CommonPageHeader>
        <CommonPageTitle>{t("menu.subMenu.site")}</CommonPageTitle>
        <ButtonGroup>
          {canCreateSite && (
            <CommonButton type="outlined" onClick={bulkUpload}>
              {t("bulkUpload")}
            </CommonButton>
          )}

          <CommonButton
            type="outlined"
            id="download-data"
            style={{
              backgroundColor: "none",
              cursor: exportBtnState == "none" ? "pointer" : "not-allowed",
            }}
            onClick={exportcsv}
          >
            {exportBtnState != "none" ? (
              <img
                src={process.env.PUBLIC_URL + "/csv_export.gif"}
                style={imgStyleLoader}
              />
            ) : (
              ""
            )}
            {`${
              exportBtnState == "none"
                ? t("exportingData")
                : exportBtnState == "exporting"
                ? t("exporting")
                : exportBtnState == "down"
                ? t("downloading")
                : null
            }`}
          </CommonButton>

          {canCreateSite && (
            <CommonButton
              type="primary"
              onClick={handleCreateSite}
              style={{ display: "inline-flex" }}
              // style={{
              //   display:
              //     SSOSettings.ssoLogin && isSSOMappingEnabled
              //       ? 'none'
              //       : 'inline-flex',
              // }}
            >
              {t("createSite")}
            </CommonButton>
          )}
        </ButtonGroup>
      </CommonPageHeader>

      <ToolbarWrapper>
        <Toolbar
          searchText={searchText}
          onSearch={(searchText, searchField) => {
            handleSearch(searchText, searchField);
          }}
          onClear={() => {
            handleClearSearch();
          }}
          multiSearchOpts={[{ name: t("siteNameText"), value: "siteName" }]}
          placeholderText={t("searchPlaceholderTextSite")}
        />
        {(canUpdateSite || canDeleteSite) && (
          <>
            <DropdownActions
              styleClassName={'manageDropSite'}
              label={t('csvModal.Manage')}
              opts={
                [
                  { name: t('csvModal.Modify'), value: 'MODIFY', active: !enableModDelete },
                  { name: t('csvModal.Delete'), value: 'DELETE', active: !enableModDelete }
                ]
              }
              onChangeAction={onChangeManageBtn}
            />
            <ActionDropDown
              label="Actions"
              disable={false}
              selectedRows={selectedData.length}
              options={[
                ...(canUpdateSite
                  ? [{ label: "Modify Details", key: "UPDATE_SITE", action: "d" }]
                  : []),
                ...(canDeleteSite
                  ? [{ label: "Delete", key: "DELETE", action: "d" }]
                  : []),
              ]}
              GetChangeAction={handleActionChange}
              clrSelectedRows={() => {
                setSelectAll(false);
                setSelectedData([]);
                clearSelection();
              }}
            />
          </>
        )}
      </ToolbarWrapper>

      <MainContent>
        <Table
          columns={columns}
          data={data}
          isLoading={isLoading}
          orderBy={orderBy}
          orderDirection={orderDirection}
          isSearchActive={!!searchText}
          searchField={searchField}
          onSort={(field, direction) => {
            handleSort(field, direction);
          }}
          pagination={{
            itemCount,
            currentPage,
            rowsPerPage: pageSize,
            rowsPerPageOptions: [10, 20, 50],
            onNextPageClicked: next,
            onPrevPageClicked: prev,
            onFirstPageClicked: handleFirstPage,
            onLastPageClicked: handleLastPage,
            onChangeRowsPerPage: (event) => {
              const newSize = event.target.value;
              setPageSize(newSize);
              setCursors(null);
              getData(
                newSize,
                orderBy,
                orderDirection,
                false,
                searchText,
                searchField
              );
            },
            hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE,
          }}
          actions={[
            canDeleteSite &&
              ((rowData) => ({
                hidden:
                  rowData.accessType in [ROLES.MANAGER, ROLES.COMPANY_USER],
                icon: () => <DeleteIcon />,
                onClick: (event, rowData) => {
                  setDeletedSite(rowData);
                  setDeletedSiteName(rowData.siteName);
                  confirmModalRef.current.toggleDialog();
                },
                tooltip: t("deleteSite"),
              })),
            canUpdateSite &&
              ((rowData) => ({
                hidden: rowData.accessType in [ROLES.COMPANY_USER],
                icon: () => <EditIcon />,
                onClick: (event, rowData) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSiteEdit(rowData);
                },
                tooltip: t("editSite"),
              })),
          ].filter(Boolean)}
          emptyMessage={(isLoading && data.length === 0) ? "" : t("messages.error.err58")}
        />
      </MainContent>

      <BottomNavLinks />

      {(canCreateSite || canUpdateSite) && (
        <AddNewSiteModal
          modalRef={addSiteModalRef}
          initialData={selectedSite}
          appSetting={apsData}
          data={data}
          onClose={(status) => onCloseSiteModal(status, selectedSite)}
        />
      )}

      {canUpdateSite && (
        <EditSiteModal
          modalRef={editSiteModalRef}
          selectedItems={selectedData}
          filterQuery={getQuery}
          showAllSites={selectAll}
          tenantId={companyID}
          appSetting={apsData}
          data={data}
          onSitesUpdated={async () => {
            //Refreh the page
            var msg = selectAll
              ? "All Sites updated"
              : selectedData.length + " Sites updated";

            await getData(pageSize, orderBy, orderDirection, true);
            clearSelection();

            setSnackBarMessage(msg);
            setSnackBarVisible(true);
          }}
        />
      )}

      {canDeleteSite && (
        <>
          <ConfirmationModal
            title={confirmPopupData.title}
            body={confirmPopupData.msg}
            modalRef={confirmationModalRef}
            btnData={confirmPopupData.btnData}
            topMsg={confirmPopupData.topMsg}
            needcheck={confirmPopupData.needcheck}
            checktext={confirmPopupData.checktext}
            id={"ConfirmationModal"}
            fromConfirmationPopup={handleConfirmationPopup}
          />
        </>
      )}

      {canDeleteSite && (
        <ConfirmModal
          modalRef={confirmModalRef}
          title={t("deleteSite")}
          message={t("deleteSiteConfirmationMsg", { deletedSiteName })}
          onConfirm={() => {
            confirmModalRef.current.toggleDialog();
            handleDeleteSite(deletedSite);
          }}
        />
      )}

      {(canCreateSite || canUpdateSite || canDeleteSite) && (
        <CsvCompModal
          modalRef={CsvCompModalRef}
          importType={manageBtnValue}
          ConfigData={
            apsData.deviceRegistrationType === 2
              ? SitesConfigDataWithIP
              : SitesConfigData
          }
          deviceRegistrationType={apsData.deviceRegistrationType}
          refreshPage={refreshPageClicked}
          // onCancelHit={onCancelHit}
        />
      )}

      <AlertModal modalRef={alertModalRef} message={errorMessage} />

      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={snackbarVisible}
        autoHideDuration={5000}
        onClose={() => setSnackBarVisible(false)}
      >
        <SnackbarContent error={error}>{snackbarMessage}</SnackbarContent>
      </Snackbar>
    </ContentWrapper>
  );
}

export default withRBAC(ManageSitesPage, RESOURCES.SITE_MANAGEMENT);
